import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Paper,
  Stack,
  Avatar,
  Badge,
  Button,
  ButtonGroup,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  ClickAwayListener,
  List,
  ListItem
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  CheckCircle as ApprovedIcon,
  Cancel as PendingIcon,
  ArrowBack as ArrowBackIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  WatchLater as WatchLaterIcon,
  Close as RejectedIcon,
  Block as BlockIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import RaceChip from '../components/RaceChip';
import useMatchActions from '../hooks/useMatchActions';

// Función para obtener el color del tier
const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

// Función para obtener el icono del highlight
const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

// Función para generar el historial de eventos
const generateEventHistory = (match, usersMap) => {
  if (!match || !usersMap) return [];

  console.log('Generando historial de eventos para match:', {
    matchId: match.id,
    team1: match.team1?.players,
    team2: match.team2?.players,
    usersMap
  });

  const events = [];

  // Evento de creación
  if (match.created_at) {
    console.log('Agregando evento de creación:', {
      createdAt: match.created_at,
      createdBy: match.created_by,
      creatorName: usersMap[match.created_by]
    });
    events.push({
      type: 'creation',
      date: match.created_at,
      user: usersMap[match.created_by] || 'Usuario desconocido',
      text: 'Partida creada'
    });

    // Verificar si el creador es jugador y aprobó inicialmente
    const creatorId = match.created_by;
    const isCreatorInTeam1 = match.team1?.players?.some(p => p.user_id === creatorId);
    const isCreatorInTeam2 = match.team2?.players?.some(p => p.user_id === creatorId);
    
    console.log('Verificando aprobación inicial del creador:', {
      creatorId,
      isCreatorInTeam1,
      isCreatorInTeam2
    });

    if ((isCreatorInTeam1 || isCreatorInTeam2) && match.created_at) {
      events.push({
        type: 'initial_approval',
        date: match.created_at,
        user: usersMap[creatorId] || 'Usuario desconocido',
        text: 'Aprobación inicial por el creador'
      });
    }
  }

  // Eventos de aprobación por equipo
  if (match.team1?.players) {
    console.log('Procesando aprobaciones del equipo 1:', match.team1.players);
    match.team1.players.forEach(player => {
      console.log('Verificando jugador del equipo 1:', {
        userId: player.user_id,
        username: usersMap[player.user_id],
        status: player.approval_status,
        updatedAt: player.status_updated_at
      });
      
      // Solo agregar aprobación si no es el creador (ya que se agregó como aprobación inicial)
      if (player.approval_status === 'approved' && player.status_updated_at && player.user_id !== match.created_by) {
        events.push({
          type: 'team_approval',
          date: player.status_updated_at,
          user: usersMap[player.user_id] || 'Usuario desconocido',
          text: `${usersMap[player.user_id] || 'Usuario desconocido'} aprobó la partida`
        });
      }
    });
  }

  if (match.team2?.players) {
    console.log('Procesando aprobaciones del equipo 2:', match.team2.players);
    match.team2.players.forEach(player => {
      console.log('Verificando jugador del equipo 2:', {
        userId: player.user_id,
        username: usersMap[player.user_id],
        status: player.approval_status,
        updatedAt: player.status_updated_at
      });
      
      // Solo agregar aprobación si no es el creador (ya que se agregó como aprobación inicial)
      if (player.approval_status === 'approved' && player.status_updated_at && player.user_id !== match.created_by) {
        events.push({
          type: 'team_approval',
          date: player.status_updated_at,
          user: usersMap[player.user_id] || 'Usuario desconocido',
          text: `${usersMap[player.user_id] || 'Usuario desconocido'} aprobó la partida`
        });
      }
    });
  }

  // Evento de rechazo (buscar en cualquiera de los dos equipos)
  const rejectedPlayer = match.team1?.players?.find(p => p.approval_status === 'rejected') || 
                        match.team2?.players?.find(p => p.approval_status === 'rejected');

  if (rejectedPlayer) {
    const teamNumber = match.team1?.players?.some(p => p.user_id === rejectedPlayer.user_id) ? 1 : 2;
    events.push({
      type: 'rejection',
      date: rejectedPlayer.status_updated_at || match.approval_updated_at,
      user: usersMap[rejectedPlayer.user_id] || 'Usuario desconocido',
      text: `${usersMap[rejectedPlayer.user_id] || 'Usuario desconocido'} rechazó la partida`
    });
  }

  // Evento de cancelación si aplica
  if (match.approval_status === 'cancelled' && match.approval_updated_at) {
    events.push({
      type: 'cancellation',
      date: match.approval_updated_at,
      user: usersMap[match.last_updated_by] || 'Usuario desconocido',
      text: 'Partida cancelada'
    });
  }

  console.log('Eventos generados antes de ordenar y filtrar:', events);
  
  // Ordenar eventos por fecha y eliminar duplicados
  const filteredEvents = events
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .filter((event, index, self) => 
      index === self.findIndex(e => 
        e.type === event.type && 
        e.user === event.user && 
        e.date === event.date
      )
    );

  console.log('Eventos finales después de ordenar y filtrar:', filteredEvents);
  
  return filteredEvents;
};

function MatchDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [match, setMatch] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [decksMap, setDecksMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessed, setIsProcessed] = useState(false);
  const { loading: actionLoading, handleApprove, handleReject, handleCancel, handleCreateNewFromRejected: handleCreateNew, needsUserApproval } = useMatchActions(null);
  const [openTooltip, setOpenTooltip] = useState(null);
  const [isProcessingRejection, setIsProcessingRejection] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch match details
        const matchResponse = await api.get(`/api/matches/${id}`);
        setMatch(matchResponse.data);

        // Fetch users
        const usersResponse = await api.get('/api/users');
        const usersData = {};
        usersResponse.data.forEach(user => {
          usersData[user.id] = user.username;
        });
        setUsersMap(usersData);

        // Fetch decks if needed
        const deckIds = new Set();
        if (matchResponse.data.team1?.decks) {
          matchResponse.data.team1.decks.forEach(deck => deckIds.add(deck.deck_id));
        }
        if (matchResponse.data.team2?.decks) {
          matchResponse.data.team2.decks.forEach(deck => deckIds.add(deck.deck_id));
        }

        if (deckIds.size > 0) {
          const decksResponse = await api.get('/api/decks');
          const decksData = {};
          decksResponse.data.forEach(deck => {
            if (deckIds.has(deck.id)) {
              decksData[deck.id] = deck;
            }
          });
          setDecksMap(decksData);
        }
      } catch (error) {
        console.error('Error fetching match details:', error);
        setError('Error al cargar los detalles de la partida');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getPlayerDecks = (teamNumber) => {
    const team = teamNumber === 1 ? match.team1 : match.team2;
    if (!team?.decks) return [];
    
    return team.decks.map(deck => {
      if (!deck) {
        console.log(`No deck found for team ${teamNumber}`);
        return {
          name: 'Mazo no disponible',
          race: 'Desconocida'
        };
      }
      
      if (deck.deck_raza) {
        return {
          name: deck.deck_name || 'Mazo sin nombre',
          race: deck.deck_raza
        };
      }
      
      const deckInfo = decksMap[deck.deck_id];
      if (deckInfo) {
        return {
          name: deckInfo.name || 'Mazo sin nombre',
          race: deckInfo.raza
        };
      }
      
      console.log(`Deck ${deck.deck_id} not found in decksMap for team ${teamNumber}`);
      return {
        name: `Mazo Eliminado (ID: ${deck.deck_id})`,
        race: 'Desconocida'
      };
    }).filter(Boolean);
  };

  const getPlayerApprovalStatus = (playerId, teamNumber) => {
    try {
      const team = teamNumber === 1 ? match.team1 : match.team2;
      if (!team?.players) return false;
      
      const player = team.players.find(p => p.user_id === playerId);
      return player?.approval_status;
    } catch (error) {
      console.warn('Error checking player approval status:', error);
      return false;
    }
  };

  const getUserTeam = () => {
    try {
      if (!match.team1?.players || !match.team2?.players) return null;
      
      const isInTeam1 = match.team1.players.some(p => p.user_id === user.id);
      const isInTeam2 = match.team2.players.some(p => p.user_id === user.id);
      
      if (isInTeam1) return 1;
      if (isInTeam2) return 2;
      return null;
    } catch (error) {
      console.warn('Error getting user team:', error);
      return null;
    }
  };

  const handleApproveClick = async () => {
    try {
      const success = await handleApprove(match.id);
      if (success) {
        // Recargar los datos de la partida
        const updatedMatch = await api.get(`/api/matches/${id}`);
        setMatch(updatedMatch.data);
        // Forzar actualización del estado
        setIsProcessed(false);
      }
    } catch (error) {
      console.error('Error al aprobar la partida:', error);
    }
  };

  const handleRejectClick = async () => {
    try {
      const success = await handleReject(match.id);
      if (success) {
        // Recargar los datos de la partida
        const updatedMatch = await api.get(`/api/matches/${id}`);
        setMatch(updatedMatch.data);
        // Forzar actualización del estado
        setIsProcessed(false);
      }
    } catch (error) {
      console.error('Error al rechazar la partida:', error);
    }
  };

  const handleRejectionAccept = async () => {
    setIsProcessingRejection(true);
    try {
      await handleCancel(match.id);
      // Recargar los datos de la partida
      const updatedMatch = await api.get(`/api/matches/${id}`);
      setMatch(updatedMatch.data);
      setIsProcessed(true);
    } catch (error) {
      console.error('Error al cancelar la partida:', error);
    } finally {
      setIsProcessingRejection(false);
    }
  };

  const handleCreateNewFromRejected = async () => {
    setIsProcessingRejection(true);
    try {
      await handleCreateNew(match);
      // No need to handle navigation here as it's handled in the hook
    } catch (error) {
      console.error('Error al crear nueva partida:', error);
    } finally {
      setIsProcessingRejection(false);
    }
  };

  const handleTooltipOpen = (tooltipId) => {
    setOpenTooltip(tooltipId);
  };

  const handleTooltipClose = () => {
    setOpenTooltip(null);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 120px)' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !match) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: 2,
        p: 3 
      }}>
        <Typography color="error">{error || 'Partida no encontrada'}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Volver
        </Button>
      </Box>
    );
  }

  const team1Players = match.team1?.players || [];
  const team2Players = match.team2?.players || [];
  const isTeam1Winner = match.winning_team === 'team1';
  const winnerColor = '#1976d2';
  const isRejected = match.approval_status === 'rejected';
  const isPending = match.approval_status === 'pending';
  const isCancelled = match.approval_status === 'cancelled';
  const isProcessedMatch = isProcessed || match.team1?.players?.some(p => p.approval_status === 'processed') || match.team2?.players?.some(p => p.approval_status === 'processed');
  const isApproved = !isRejected && !isPending && !isCancelled;

  console.log('Match status:', {
    status: match.approval_status,
    team1Status: match.team1?.players?.map(p => p.approval_status),
    team2Status: match.team2?.players?.map(p => p.approval_status),
    isRejected,
    isPending,
    isApproved
  });

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ 
            p: 1.8,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isCancelled ? 'rgba(158, 158, 158, 0.5)' :
                     isRejected ? 'rgba(244, 67, 54, 0.5)' : 
                     isPending ? 'rgba(255, 193, 7, 0.5)' : 'divider',
            bgcolor: isCancelled ? 'rgba(158, 158, 158, 0.05)' :
                     isRejected ? 'rgba(244, 67, 54, 0.05)' : 
                     isPending ? 'rgba(255, 193, 7, 0.05)' : 'background.paper'
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => navigate(-1)}
                sx={{ 
                  width: 40,
                  height: 40,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 24 }} />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Detalles de la Partida
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  letterSpacing: '0.5px'
                }}
              >
                ID: {match.id}
              </Typography>
              {isCancelled ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Cancelada"
                    color="default"
                    size="medium"
                    icon={<BlockIcon sx={{ fontSize: '1.2rem' }} />}
                    sx={{ 
                      height: '32px',
                      '& .MuiChip-label': { 
                        fontSize: '0.9rem',
                        px: 1.5
                      }
                    }}
                  />
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip 
                      title="Partida cancelada. No se registrará en estadísticas"
                      arrow
                      placement="top"
                      open={openTooltip === 'cancelled'}
                      onClose={handleTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleTooltipOpen('cancelled')}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <InfoIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                </Box>
              ) : isRejected ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Rechazada"
                    color="error"
                    size="medium"
                    icon={<RejectedIcon sx={{ fontSize: '1.2rem' }} />}
                    sx={{ 
                      height: '32px',
                      '& .MuiChip-label': { 
                        fontSize: '0.9rem',
                        px: 1.5
                      }
                    }}
                  />
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip 
                      title="Partida rechazada. Puedes aceptar el rechazo o crear una nueva con los mismos datos. Ambas acciones cancelan la partida."
                      arrow
                      placement="top"
                      open={openTooltip === 'rejected'}
                      onClose={handleTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleTooltipOpen('rejected')}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <InfoIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                </Box>
              ) : isPending ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Pendiente"
                    icon={<WatchLaterIcon sx={{ fontSize: '1.2rem' }} />}
                    size="medium"
                    sx={{ 
                      height: '32px',
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      '& .MuiChip-label': { 
                        fontSize: '0.9rem',
                        px: 1.5
                      }
                    }}
                  />
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip 
                      title="Esperando aprobación de todos los jugadores para registrar en estadísticas"
                      arrow
                      placement="top"
                      open={openTooltip === 'pending'}
                      onClose={handleTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleTooltipOpen('pending')}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <InfoIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                </Box>
              ) : isApproved && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label="Aprobada"
                    color="success"
                    size="medium"
                    icon={<ApprovedIcon sx={{ fontSize: '1.2rem' }} />}
                    sx={{ 
                      height: '32px',
                      '& .MuiChip-label': { 
                        fontSize: '0.9rem',
                        px: 1.5
                      }
                    }}
                  />
                  <ClickAwayListener onClickAway={handleTooltipClose}>
                    <Tooltip 
                      title="Partida aprobada y registrada en estadísticas"
                      arrow
                      placement="top"
                      open={openTooltip === 'approved'}
                      onClose={handleTooltipClose}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleTooltipOpen('approved')}
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <InfoIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                </Box>
              )}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(match.match_date), "d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GameIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {match.match_mode}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrophyIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {match.tier}
                </Typography>
              </Box>
            </Stack>

            {/* Highlights */}
            {match.highlights?.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: 'text.secondary', fontSize: '0.72rem', mt: 0.3 }}
                >
                  Highlights:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {match.highlights.map((highlight, index) => (
                    <Chip
                      key={index}
                      icon={getHighlightIcon(highlight.type)}
                      label={highlight.description}
                      size="small"
                      sx={{ 
                        height: '24px',
                        bgcolor: '#0277bd',
                        color: 'white',
                        '& .MuiChip-label': { 
                          fontSize: '0.75rem',
                          px: 1
                        },
                        '& .MuiChip-icon': {
                          color: 'white'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Teams */}
        <Paper
          elevation={0}
          sx={{ 
            p: 1.8,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isCancelled ? 'rgba(158, 158, 158, 0.5)' :
                     isRejected ? 'rgba(244, 67, 54, 0.5)' : 
                     isPending ? 'rgba(255, 193, 7, 0.5)' : 'divider',
            bgcolor: isCancelled ? 'rgba(158, 158, 158, 0.05)' :
                     isRejected ? 'rgba(244, 67, 54, 0.05)' : 
                     isPending ? 'rgba(255, 193, 7, 0.05)' : 'background.paper'
          }}
        >
          <Stack spacing={2.5}>
            {[
              { 
                players: team1Players, 
                isWinner: isTeam1Winner, 
                num: 1,
                score: match.team1?.score || 0
              },
              { 
                players: team2Players, 
                isWinner: !isTeam1Winner, 
                num: 2,
                score: match.team2?.score || 0
              }
            ].map((team, idx) => (
              <Box key={idx}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: team.isWinner ? winnerColor : 'text.secondary',
                      fontSize: '1.8rem',
                      minWidth: '50px',
                      textAlign: 'center'
                    }}
                  >
                    {team.score}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    {team.players.map((player, playerIdx) => (
                      <Box 
                        key={player.user_id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          flex: 1
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {(() => {
                                  const status = getPlayerApprovalStatus(player.user_id, team.num);
                                  if (status === 'rejected') {
                                    return (
                                      <RejectedIcon
                                        sx={{
                                          fontSize: 14,
                                          color: '#f44336',
                                        }}
                                      />
                                    );
                                  } else if (status === 'approved') {
                                    return (
                                      <ApprovedIcon
                                        sx={{
                                          fontSize: 14,
                                          color: 'success.main',
                                        }}
                                      />
                                    );
                                  } else if (status === 'cancelled') {
                                    return (
                                      <BlockIcon
                                        sx={{
                                          fontSize: 14,
                                          color: 'grey.600',
                                        }}
                                      />
                                    );
                                  }
                                  return (
                                    <WatchLaterIcon
                                      sx={{
                                        fontSize: 14,
                                        color: 'warning.main',
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            }
                          >
                            <IconButton
                              onClick={() => navigate(`/user/${player.user_id}`)}
                              sx={{
                                p: 0,
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  transition: 'transform 0.2s ease-in-out'
                                }
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: team.isWinner ? winnerColor : 'grey.700',
                                  border: '2px solid',
                                  borderColor: team.isWinner ? winnerColor : 'grey.500',
                                  color: 'white',
                                  fontSize: '1.1rem',
                                  cursor: 'pointer'
                                }}
                              >
                                {usersMap[player.user_id] ? usersMap[player.user_id][0]?.toUpperCase() : '?'}
                              </Avatar>
                            </IconButton>
                          </Badge>
                          <Typography
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: team.isWinner ? 'bold' : 'normal',
                              color: (() => {
                                const status = getPlayerApprovalStatus(player.user_id, team.num);
                                if (status === 'rejected') return '#f44336';
                                if (status === 'approved') return team.isWinner ? '#1976d2' : 'text.secondary';
                                return team.isWinner ? '#1976d2' : 'text.secondary';
                              })(),
                              textAlign: 'center'
                            }}
                          >
                            {usersMap[player.user_id]}
                          </Typography>
                        </Box>
                        {(() => {
                          const playerDeck = getPlayerDecks(team.num)[playerIdx];
                          if (!playerDeck?.race) return null;
                          return (
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: 'center', 
                              gap: 0.5,
                              mt: 0.2
                            }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  textAlign: 'center'
                                }}
                              >
                                {playerDeck.name}
                              </Typography>
                              <RaceChip race={playerDeck.race} size="small" />
                            </Box>
                          );
                        })()}
                      </Box>
                    ))}
                  </Box>
                </Box>
                {idx === 0 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        </Paper>

        {/* Action Buttons */}
        {(() => {
          if (isCancelled) {
            return (
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'rgba(158, 158, 158, 0.5)',
                  borderRadius: 2,
                  bgcolor: 'rgba(158, 158, 158, 0.05)'
                }}
              >
                <Button
                  variant="contained"
                  disabled
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    borderRadius: 1.5,
                    py: 1,
                    bgcolor: 'grey.600',
                    color: 'grey.100',
                    '&:hover': {
                      bgcolor: 'grey.600'
                    }
                  }}
                >
                  Partida Cancelada
                </Button>
              </Paper>
            );
          } else if (isRejected) {
            return (
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'rgba(244, 67, 54, 0.5)',
                  borderRadius: 2,
                  bgcolor: 'rgba(244, 67, 54, 0.05)'
                }}
              >
                <ButtonGroup 
                  variant="contained" 
                  sx={{ 
                    width: '100%',
                    '& .MuiButton-root': {
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      py: 1
                    }
                  }}
                >
                  <Button
                    color="success"
                    startIcon={<ApprovedIcon />}
                    onClick={handleRejectionAccept}
                    disabled={isProcessingRejection}
                  >
                    {isProcessingRejection ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Aceptar Rechazo'
                    )}
                  </Button>
                  <Button
                    color="primary"
                    startIcon={<GameIcon />}
                    onClick={handleCreateNewFromRejected}
                    disabled={isProcessingRejection}
                  >
                    {isProcessingRejection ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Crear Nueva'
                    )}
                  </Button>
                </ButtonGroup>
              </Paper>
            );
          } else if (isPending && needsUserApproval(match)) {
            return (
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'rgba(255, 193, 7, 0.5)',
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 193, 7, 0.05)'
                }}
              >
                <ButtonGroup 
                  variant="contained" 
                  sx={{ 
                    width: '100%',
                    '& .MuiButton-root': {
                      flex: 1,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      py: 1
                    }
                  }}
                >
                  <Button
                    color="success"
                    startIcon={<ApprovedIcon />}
                    onClick={handleApproveClick}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Aprobar'
                    )}
                  </Button>
                  <Button
                    color="error"
                    startIcon={<PendingIcon />}
                    onClick={handleRejectClick}
                    disabled={actionLoading}
                  >
                    Rechazar
                  </Button>
                </ButtonGroup>
              </Paper>
            );
          }
          return null;
        })()}

        {/* Event History */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Historial de Eventos</Typography>
          </Box>
          <List>
            {generateEventHistory(match, usersMap).map((event, index) => {
              const eventDate = new Date(event.date);
              const isValidDate = !isNaN(eventDate.getTime());
              
              return (
                <ListItem 
                  key={index}
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderLeft: `${index === generateEventHistory(match, usersMap).length - 1 ? '4px' : '2px'} solid`,
                    borderColor: event.type === 'creation' ? 'primary.main' :
                                event.type === 'rejection' ? 'error.main' : 
                                event.type === 'cancellation' ? 'text.secondary' :
                                event.type === 'team_approval' || event.type === 'initial_approval' ? 'success.main' : 
                                'primary.main',
                    pl: 2,
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {event.type === 'creation' && <AddIcon sx={{ mr: 1, color: 'primary.main' }} />}
                    {event.type === 'initial_approval' && <ApprovedIcon sx={{ mr: 1, color: 'success.main' }} />}
                    {event.type === 'team_approval' && <ApprovedIcon sx={{ mr: 1, color: 'success.main' }} />}
                    {event.type === 'rejection' && <RejectedIcon sx={{ mr: 1, color: 'error.main' }} />}
                    {event.type === 'cancellation' && <BlockIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                    
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {event.text}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      {isValidDate ? format(eventDate, "d 'de' MMMM 'de' yyyy 'a las' HH:mm", { locale: es }) : 'Fecha no disponible'}
                    </Typography>
                  </Box>
                  
                  {event.user && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                      por {event.user}
                    </Typography>
                  )}
                </ListItem>
              );
            })}
          </List>
        </Paper>

        {/* Created by label */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
            fontSize: '0.72rem',
            textAlign: 'center',
            display: 'block'
          }}
        >
          Creada por <Box component="span" sx={{ color: 'primary.main' }}>{usersMap[match.created_by] || 'Usuario desconocido'}</Box>
        </Typography>
      </Stack>
    </Container>
  );
}

export default MatchDetailsPage; 